import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormHelperText,
  FormLabel,
  Grid,
  IconButton,
} from "@mui/material";
import { Theme, useTheme } from "@mui/material/styles";
import DeleteEntityDialog from "components/DeleteEntityDialog";
import LazyTable, {
  DEFAULT_ROWS_PER_PAGE,
  TableCellType,
  TableSortType,
} from "components/LazyTable";
import SnackbarMessage from "components/SnackbarMessage";
import { FORM_ERROR } from "final-form";
import { makeStyles } from "helpers/StyleHelper";
import useGlobalContext from "hooks/globalData";
import useBoolean from "hooks/useBoolean";
import SelectStudyField from "pages/SkusPage/SkuStudyFields/SelectStudyField";
import { useCallback, useState } from "react";
import { Field, Form } from "react-final-form";
import { FormattedMessage as T, useIntl } from "react-intl";
import { useQuery, useQueryClient } from "react-query";
import {
  IQualificationStudyField,
  ListRequestOfQualificationStudyFieldFilter,
  ListResponseOfQualificationStudyField,
  QualificationStudyFieldSaveRequest,
} from "services/KnowfullyAdminClient";
import { SnackbarMessageProps } from "types/common-types";
import translateError from "utils/translateError";

const useStyles = makeStyles((theme: Theme) => ({
  feedback: {
    width: "100%",
    margin: "20px 0 20px 0",
  },

  lineContainer: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
}));

const QualificationStudyFieldList = ({
  qualificationId,
}: {
  qualificationId: string;
}) => {
  const styles = useStyles(useTheme());
  const { formatMessage: t } = useIntl();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);
  const [order, setOrder] = useState<TableSortType>("asc");
  const [orderBy, setOrderBy] = useState("sequence");
  const [createOpen, createOpenActions] = useBoolean(false);
  const { qualificationService } = useGlobalContext();
  const queryClient = useQueryClient();
  const [
    itemToDelete,
    setItemToDelete,
  ] = useState<IQualificationStudyField | null>(null);
  const [
    snackbarMessage,
    setSnackbarMessage,
  ] = useState<SnackbarMessageProps | null>(null);

  const DeleteButton = ({ value }: { value: any }) => {
    return (
      <Box textAlign="center">
        <IconButton size="small" onClick={() => setItemToDelete(value)}>
          <DeleteIcon />
        </IconButton>
      </Box>
    );
  };

  const { data, isFetching } = useQuery<ListResponseOfQualificationStudyField>(
    ["qualificationStudyFields", qualificationId],
    () =>
      qualificationService.getQualificationStudyFields(
        qualificationId,
        ListRequestOfQualificationStudyFieldFilter.fromJS({
          sortField: "createdOn",
        }),
      ),
  );

  const cells: Array<TableCellType> = [
    {
      accessor: "studyFieldName",
      align: "left",
      width: 0,
      type: "text",
      label: t({ id: "qualificationStudyFieldListStudyFieldNameLabel" }),
      noSort: true,
    },
    {
      accessor: "submitValidationRequired",
      align: "left",
      width: 0,
      type: "boolean",
      label: t({ id: "qualificationStudyFieldSubmitValidationRequiredLabel" }),
      noSort: true,
    },
    {
      accessor: "value",
      align: "center",
      width: 1,
      type: "node",
      View: DeleteButton,
      label: "",
      noSort: true,
    },
  ];

  const reloadTable = useCallback(
    () =>
      queryClient.invalidateQueries([
        "qualificationStudyFields",
        qualificationId,
      ]),
    [qualificationId, queryClient],
  );

  const handleSubmit: any = useCallback(
    async (formValues: QualificationStudyFieldSaveRequest) => {
      try {
        await qualificationService.saveQualificationStudyField(
          qualificationId,
          formValues,
        );

        createOpenActions.setFalse();

        setSnackbarMessage(
          new SnackbarMessageProps(
            t({ id: "qualificationStudyFieldAssociatedSuccess" }),
          ),
        );

        reloadTable();
      } catch (error) {
        return { [FORM_ERROR]: translateError(error, t) };
      }
    },
    [createOpenActions, qualificationId, qualificationService, reloadTable, t],
  );

  const handleDelete = useCallback(async () => {
    const studyFieldId = itemToDelete?.studyFieldId;
    setItemToDelete(null);
    if (studyFieldId) {
      try {
        await qualificationService.deleteQualificationStudyField(
          qualificationId,
          studyFieldId,
        );

        setSnackbarMessage(
          new SnackbarMessageProps(
            t({ id: "qualificationStudyFieldRemovedSuccess" }),
          ),
        );

        reloadTable();
      } catch (error) {
        return { [FORM_ERROR]: translateError(error, t) };
      }
    }
  }, [
    itemToDelete?.studyFieldId,
    qualificationId,
    qualificationService,
    reloadTable,
    t,
  ]);

  return (
    <Box>
      <Accordion sx={{ mt: 3 }} defaultExpanded>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <T id="qualificationStudyFieldsListLabel" />
        </AccordionSummary>
        <AccordionDetails>
          <LazyTable
            {...{
              rowCount: data?.totalRecords ?? 0,
              rows: (data?.data as IQualificationStudyField[]) ?? [],
              loading: isFetching,
              allowDragAndDrop: false,
              dense: true,
              titleComponent: (
                <Box display="flex" justifyContent="flex-end" width="100%">
                  <Button
                    onClick={createOpenActions.setTrue}
                    startIcon={<AddIcon />}
                    data-cy="addQualStudyFieldButton"
                  >
                    <T id="qualificationAddStudyFieldButtonLabel" />
                  </Button>
                </Box>
              ),
              cells,
              allowPagination: false,
              page,
              setPage,
              rowsPerPage,
              setRowsPerPage,
              order,
              setOrder,
              orderBy,
              setOrderBy,
            }}
          />
        </AccordionDetails>
      </Accordion>
      <Dialog open={createOpen} disableEnforceFocus maxWidth="sm" fullWidth>
        <Form
          onSubmit={handleSubmit}
          render={({ handleSubmit, submitting, submitError }) => {
            return (
              <form onSubmit={handleSubmit} style={{ width: "100%" }}>
                <DialogTitle>
                  <T id="qualificationStudyFieldAddTitle" />
                </DialogTitle>
                <DialogContent dividers>
                  <Box mb={3}>
                    <Grid container spacing={3}>
                      <Field name="studyFieldId" required>
                        {({ input, meta }) => (
                          <Grid item xs={12}>
                            <FormControl
                              fullWidth
                              error={meta.error && meta.submitFailed}
                            >
                              <SelectStudyField
                                {...input}
                                required={true}
                                textFieldProps={{
                                  error: meta.error && meta.submitFailed,
                                }}
                              />
                              {meta.error && meta.submitFailed && (
                                <FormHelperText>{meta.error}</FormHelperText>
                              )}
                            </FormControl>
                          </Grid>
                        )}
                      </Field>

                      <Field name="submitValidationRequired" type="checkbox">
                        {({ input }) => (
                          <Grid item xs={12}>
                            <Box sx={styles.lineContainer}>
                              <FormLabel>
                                <T id="studyFieldSubmitValidationRequired" />
                              </FormLabel>
                              <Checkbox
                                {...input}
                                data-cy="submitValidationRequired"
                              />
                            </Box>
                          </Grid>
                        )}
                      </Field>

                      {submitError && (
                        <Alert sx={styles.feedback} severity="error">
                          {submitError}
                        </Alert>
                      )}
                    </Grid>
                  </Box>
                </DialogContent>

                <DialogActions>
                  {submitting && <CircularProgress size={24} />}
                  <Button
                    disabled={submitting}
                    onClick={() => createOpenActions.setFalse()}
                    data-cy="qualStudyFieldSCancelButton"
                  >
                    {t({ id: "cancelButton" })}
                  </Button>

                  <Button
                    type="submit"
                    color="primary"
                    disabled={submitting}
                    data-cy="qualStudyFieldSaveButton"
                  >
                    {t({ id: "saveButton" })}
                  </Button>
                </DialogActions>
              </form>
            );
          }}
        ></Form>
      </Dialog>

      <DeleteEntityDialog
        dialogTitleKey="qualificationStudyFieldDeleteTitle"
        contentKey="qualificationStudyFieldRemove"
        open={!!itemToDelete}
        handleDelete={handleDelete}
        onCancel={() => setItemToDelete(null)}
      />

      <SnackbarMessage
        snackbarMessage={snackbarMessage}
        setSnackbarMessage={setSnackbarMessage}
      />
    </Box>
  );
};

export default QualificationStudyFieldList;
